<template>
  <div
    class="rs-chip"
    :class="chipClasses"
  >
    <span class="rs-chip__content">
      <slot />
    </span>
    <span
      v-if="dismissible"
      class="rs-chip__dismissible"
    >
      <RsIcon icon="times" />
    </span>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'RsChip',

  props: {
    /**
     * Color variants: primary | secondary | success | warning | error | info | dark | light
     *
     */
    variant: {
      type: String,
      default: 'primary',
      validator: (prop?: string) => [
        'primary',
        'secondary',
        'success',
        'warning',
        'error',
        'info',
        'dark',
        'light',
      ].includes(prop ?? 'primary'),
    },
    /**
      * Lightens the chosen color variant.
      */
    soft: {
      type: Boolean,
      default: false,
    },
    /**
        * Adds a close icon that can hide the chip.
        */
    dismissible: {
      type: Boolean,
      default: false,
    },
    /**
        * Specify a custom tag used on the root element.
        */
    tag: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    chipClasses(): Record<string, boolean> {
      return {
        'rs-chip--tag': this.tag,
        'rs-chip--soft': this.soft,
        'rs-chip--primary': this.variant === 'primary',
        'rs-chip--secondary': this.variant === 'secondary',
        'rs-chip--success': this.variant === 'success',
        'rs-chip--warning': this.variant === 'warning',
        'rs-chip--error': this.variant === 'error',
        'rs-chip--info': this.variant === 'info',
        'rs-chip--light': this.variant === 'light',
        'rs-chip--dark': this.variant === 'dark',
      };
    },
  },
});
</script>

<style lang="scss" scoped>
  @import '@/styles/_variables.scss';
  .rs-chip{
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0 0.875rem;
    max-width: 100%;
    height: 2rem;
    line-height: 1.4rem;
    font-size: 0.875rem;
    border-radius: 1rem;
    background-color: $tertiary-muted;
    @each $name, $color in $colors {
      &--#{$name} {
        color: darken($color, 40%);
        background-color: $color;
        &.rs-chip--soft{
          color: darken($color, 15%);
          background-color: rgba($color, 0.5);
          font-weight: 400;
        }
      }
    }
    &--primary,
    &--secondary,
    &--dark{
      color: $white;
    }
    &--tag{
      border-radius: $global-radius;
      .rs-avatar{
        border-radius: $global-radius;
      }
    }
    &__content{
      align-items: center;
      display: inline-flex;
      height: 100%;
      max-width: 100%;
    }
    &__dismissible{
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 0 -0.6rem 0 0.5rem;
      width: 1.3rem;
      height: 1.3rem;
      border-radius: 50%;
      font-size: .8rem;
      color: $white;
      cursor: pointer;
      background-color: rgba(0,0,0,0.2);
      overflow: hidden;
      span{
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%,-50%) scale(1);
        color: rgb(255, 255, 255);
      }
    }
    .rs-avatar{
      margin: 0 0.5rem 0 -0.6rem;
      width: 1.5rem;
      height: 1.5rem;
      font-size: 0.8rem;
    }
  }
</style>
